import { useEffect, useMemo, useRef, useState } from 'react';
import { usePeople } from '../../lib/usePeople.js';

const MAX_RESULTS = 8;
export const MAX_COMMENT_MENTIONS = 25;

function mentionAtCursor(value, cursor) {
  const beforeCursor = String(value || '').slice(0, cursor ?? String(value || '').length);
  const match = beforeCursor.match(/(^|\s)@([A-Za-z0-9._'-]*)$/);
  if (!match) return null;
  return {
    start: beforeCursor.lastIndexOf('@'),
    end: beforeCursor.length,
    query: match[2] || '',
  };
}

function personEmail(person) {
  return String(person?.workEmail || person?.email || '').trim();
}

function personName(person) {
  return String(person?.displayName || person?.name || '').trim();
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function MentionCommentComposer({
  id,
  value,
  onChange,
  mentionedUserIds,
  onMentionedUserIdsChange,
  rows = 3,
  placeholder,
}) {
  const { people, loaded, error } = usePeople();
  const textareaRef = useRef(null);
  const [cursor, setCursor] = useState(() => String(value || '').length);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissedValue, setDismissedValue] = useState(null);
  const selectedIds = useMemo(
    () => (Array.isArray(mentionedUserIds) ? mentionedUserIds : []),
    [mentionedUserIds],
  );
  const atMentionLimit = selectedIds.length >= MAX_COMMENT_MENTIONS;
  const activeMention = mentionAtCursor(value, cursor);
  const open = Boolean(activeMention && !atMentionLimit && dismissedValue !== value);

  const matches = useMemo(() => {
    if (!open || !loaded || error || atMentionLimit) return [];
    const query = activeMention.query.toLowerCase();
    return people
      .filter((person) => person?.id && !selectedIds.includes(person.id))
      .filter((person) => {
        if (!query) return true;
        return `${personName(person)} ${personEmail(person)}`.toLowerCase().includes(query);
      })
      .slice(0, MAX_RESULTS);
  }, [activeMention?.query, atMentionLimit, error, loaded, open, people, selectedIds]);

  useEffect(() => {
    setActiveIndex(0);
  }, [activeMention?.query, matches.length]);

  useEffect(() => {
    if (!String(value || '').trim() && selectedIds.length) onMentionedUserIdsChange([]);
  }, [onMentionedUserIdsChange, selectedIds.length, value]);

  function updateValue(nextValue, nextCursor = nextValue.length) {
    setDismissedValue(null);
    setCursor(nextCursor);
    onChange(nextValue);
  }

  function selectPerson(person) {
    if (!activeMention || !person?.id || atMentionLimit || selectedIds.includes(person.id)) {
      return;
    }
    const name = personName(person) || 'Teammate';
    const before = String(value || '').slice(0, activeMention.start);
    const after = String(value || '')
      .slice(activeMention.end)
      .replace(/^\s*/, '');
    const suffix = after ? ` ${after}` : ' ';
    const nextValue = `${before}@${name}${suffix}`;
    const nextCursor = before.length + name.length + 2;
    onMentionedUserIdsChange([...selectedIds, person.id]);
    updateValue(nextValue, nextCursor);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  function removePerson(person) {
    const name = personName(person);
    onMentionedUserIdsChange(selectedIds.filter((userId) => userId !== person.id));
    if (name) {
      const token = new RegExp(`@${escapeRegExp(name)}\\s*`, 'g');
      const nextValue = String(value || '')
        .replace(token, '')
        .replace(/ {2,}/g, ' ');
      updateValue(nextValue, nextValue.length);
    }
    requestAnimationFrame(() => textareaRef.current?.focus());
  }

  function handleKeyDown(event) {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setDismissedValue(value);
      return;
    }
    if (!matches.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % matches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + matches.length) % matches.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      selectPerson(matches[activeIndex]);
    }
  }

  const selectedPeople = selectedIds
    .map((userId) => people.find((person) => person.id === userId))
    .filter(Boolean);
  const listboxId = `${id}-mention-results`;
  const mentionLimitId = `${id}-mention-limit`;
  const activeOptionId =
    open && matches[activeIndex] ? `${listboxId}-option-${activeIndex}` : undefined;

  return (
    <div className="mention-composer">
      <textarea
        ref={textareaRef}
        id={id}
        className="form-input form-textarea mention-composer__input"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-haspopup="listbox"
        aria-describedby={atMentionLimit ? mentionLimitId : undefined}
        aria-activedescendant={activeOptionId}
        value={value ?? ''}
        rows={rows}
        placeholder={placeholder}
        onChange={(event) => updateValue(event.target.value, event.target.selectionStart)}
        onClick={(event) => setCursor(event.currentTarget.selectionStart)}
        onKeyUp={(event) => {
          if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(event.key)) {
            setCursor(event.currentTarget.selectionStart);
          }
        }}
        onKeyDown={handleKeyDown}
      />

      {selectedPeople.length ? (
        <div className="mention-composer__selected" aria-label="People mentioned">
          {selectedPeople.map((person) => (
            <span className="mention-composer__chip" key={person.id}>
              <span>@{personName(person)}</span>
              <button
                type="button"
                aria-label={`Remove ${personName(person)} mention`}
                onClick={() => removePerson(person)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {atMentionLimit ? (
        <div id={mentionLimitId} className="mention-composer__status" role="status">
          You can mention up to {MAX_COMMENT_MENTIONS} people in one comment.
        </div>
      ) : null}

      {open && !atMentionLimit && !loaded ? (
        <div className="mention-composer__status" role="status">
          Searching people…
        </div>
      ) : null}
      {open && !atMentionLimit && loaded && error ? (
        <div className="mention-composer__status is-error" role="status">
          People search is unavailable. Try again.
        </div>
      ) : null}
      {open && !atMentionLimit && loaded && !error && !matches.length ? (
        <div className="mention-composer__status" role="status">
          No people match “{activeMention.query}”.
        </div>
      ) : null}
      {open && !atMentionLimit && loaded && !error && matches.length ? (
        <div
          id={listboxId}
          className="mention-composer__results"
          role="listbox"
          aria-label="Mention people"
        >
          {matches.map((person, index) => {
            const email = personEmail(person);
            return (
              <button
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={index === activeIndex ? 'is-active' : ''}
                key={person.id}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectPerson(person)}
              >
                <span>{personName(person)}</span>
                <small>{email || person.title || 'PRIME teammate'}</small>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
